/**
 * Persistent WebSocket client for the AVTR-1 streamer (picture mode) — a NAT-proof
 * alternative to AvtrWebRtcClient.
 *
 * Instead of negotiating WebRTC (which needs STUN/TURN to cross 4G/CGNAT/VPN NAT), this
 * opens a plain WebSocket to the streamer's /ws/stream endpoint and receives the avatar as
 * a fragmented-MP4 byte stream, played through a <video> via MediaSource (MSE). The browser
 * dials OUT, so it connects through any NAT/firewall with zero traversal. Trade-off: TCP
 * latency instead of WebRTC-UDP.
 *
 * Same conversation flow as the WebRTC path: the backend still pushes TTS PCM into the live
 * session via the streamer's /speak endpoint, and /interrupt still barges in. Only the
 * media transport differs, so this exposes the same surface AvtrWebRtcClient does.
 */

import { logger } from "../common/logger";

export interface AvtrWsConnectOptions {
  baseUrl: string; // http(s)://host:port of the streamer
  videoEl: HTMLVideoElement;
  avatarId: string;
  backgroundId: string;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onError?: (err: unknown) => void;
  /** Fires when the avatar's audio starts/stops (tapped off the <video> via WebAudio). */
  onSpeaking?: (speaking: boolean) => void;
}

// baseline@3.1 H.264 + AAC-LC — must match what the streamer's muxer emits.
const MSE_MIME = 'video/mp4; codecs="avc1.42E01F, mp4a.40.2"';
// Jitter buffer: build up this much media before starting (and after any stall) so delivery gaps
// don't starve playback. This is a floor on how close to live we can sit, so it looks tempting to
// shrink — but it MUST exceed the streamer's delivery gap or playback cannot survive one cycle.
//
// The streamer does not trickle frames at realtime; it renders a chunk, dumps it, and waits.
// Measured at the source (fragment inter-arrival over a live session):
//     min 1ms   p50 4ms   p90 992ms   max 992ms      (193 KB/s average — realtime on average)
// i.e. fragments arrive milliseconds apart in a burst, then ~1s of nothing. The old 0.8s value was
// below that 1s gap, so an underrun every cycle was arithmetic, not bad luck — and on a LIVE
// stream each underrun is permanent drift, which is what compounded into a 40s lag.
// 1.5s clears the worst observed gap with ~50% headroom.
const TARGET_BUFFER_S = 1.5;
// After an underrun, rebuild more than TARGET_BUFFER_S before resuming: a stall is evidence this
// connection's gaps are worse than measured, and resuming on the same depth just stalls again.
const REBUFFER_S = 3.0;
// If we drift further than this behind the live edge, speed up slightly to catch back up
// (via playbackRate, which is smooth — no seeking/jumping). Only a gross desync hard-seeks.
const MAX_BUFFER_S = 2.5;
const HARD_RESEEK_S = 6.0;
// How far past the target the playhead must drift before the idle-pin corrects it.
//
// This MUST exceed one delivery burst. Because the streamer ships ~1s of media at a time, `behind`
// naturally swings by a full burst every cycle — so a slack below that is crossed on every burst
// and the pin fires forever: roughly one seek per second, each one an audible click AND a
// `waiting` event that the controller then miscounts as an underrun. A 0.5s slack produced ~770
// self-inflicted "underruns" in a single session.
const IDLE_PIN_SLACK_S = 2.5;
// Floor on time between pin seeks. Drift accrues slowly; corrections are audible. Bounding the
// rate costs a little latency and removes the churn even if the slack is ever tuned too low.
const PIN_COOLDOWN_MS = 5_000;
// A seek fires `waiting` synchronously. Ignore underruns this soon after one we caused ourselves.
const SELF_SEEK_QUIET_MS = 500;
const HEARTBEAT_MS = 20_000;

/**
 * `createMediaElementSource(el)` may be called only ONCE per <video> element for the life of the
 * page — and once called, that element's audio is permanently routed through the graph. Switching
 * avatars builds a NEW AvtrWsClient on the SAME <video>, so we must NOT create a second source (it
 * throws, and leaves the element muted through a dead context). Cache the context + source per
 * element and reuse them; each client just taps a fresh analyser off the shared source.
 */
interface ElementAudioGraph {
  ctx: AudioContext;
  source: MediaElementAudioSourceNode;
}
const elementAudioGraphs = new WeakMap<HTMLVideoElement, ElementAudioGraph>();

function getElementAudioGraph(el: HTMLVideoElement): ElementAudioGraph | null {
  const cached = elementAudioGraphs.get(el);
  if (cached) return cached;
  try {
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AudioCtx();
    const source = ctx.createMediaElementSource(el);
    source.connect(ctx.destination); // the element's audio now plays through the graph
    const graph = { ctx, source };
    elementAudioGraphs.set(el, graph);
    return graph;
  } catch (e) {
    logger.warn("[AVTR-WS] element audio graph unavailable:", e);
    return null;
  }
}

export class AvtrWsClient {
  private ws: WebSocket | null = null;
  private baseUrl = "";
  private videoEl: HTMLVideoElement | null = null;
  private mediaSource: MediaSource | null = null;
  private sourceBuffer: SourceBuffer | null = null;
  private pending: ArrayBuffer[] = [];
  /** Underruns this session. Each one is permanent drift on a live stream — see REBUFFER_S. */
  private stalls = 0;
  private lastPinAt = 0;
  /** Until when to treat `waiting` as our own seek rather than a delivery gap. */
  private selfSeekUntil = 0;
  private objectUrl: string | null = null;
  private closed = false;
  private connectedFired = false;
  private playing = false; // gated on the jitter buffer filling to TARGET_BUFFER_S

  // Speaking detection tapped off the <video> element's audio (shared graph, see cache above).
  private audioCtx: AudioContext | null = null;
  private audioSource: MediaElementAudioSourceNode | null = null;
  private analyser: AnalyserNode | null = null;
  private levelTimer: ReturnType<typeof setInterval> | null = null;
  private catchUpTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private speaking = false;
  private silenceSince = 0;
  private speakingSetup = false;
  private onSpeaking?: (speaking: boolean) => void;
  /** Fired exactly once, from disconnect(), so every teardown path notifies the owner. */
  private onDisconnected?: () => void;

  async connect(options: AvtrWsConnectOptions): Promise<void> {
    const { baseUrl, videoEl, avatarId, backgroundId } = options;
    this.baseUrl = baseUrl;
    this.videoEl = videoEl;
    this.onSpeaking = options.onSpeaking;
    this.onDisconnected = options.onDisconnected;
    this.closed = false;
    this.connectedFired = false;

    try {
      if (!("MediaSource" in window) || !MediaSource.isTypeSupported(MSE_MIME)) {
        throw new Error(`MSE cannot play ${MSE_MIME}`);
      }

      const mediaSource = new MediaSource();
      this.mediaSource = mediaSource;
      this.objectUrl = URL.createObjectURL(mediaSource);
      videoEl.src = this.objectUrl;
      videoEl.muted = true; // satisfy autoplay; unmute() flips it on a user gesture
      videoEl.playsInline = true;
      // Let playbackRate change tempo WITHOUT shifting pitch (smooth catch-up).
      (videoEl as any).preservesPitch = true;
      (videoEl as any).mozPreservesPitch = true;
      (videoEl as any).webkitPreservesPitch = true;
      // If the buffer runs dry, re-gate on the jitter buffer instead of resuming instantly
      // with a thin buffer (which just stalls again -> the repeating micro-stutter).
      const onUnderrun = (evt: string) => {
        // Setting currentTime fires `waiting` synchronously. Counting that as an underrun makes
        // the controller escalate on its own corrections — and since liveTarget() feeds the pin's
        // target, that escalation causes more seeking. Self-reinforcing, and audible.
        if (Date.now() < this.selfSeekUntil) return;
        this.stalls += 1;
        this.playing = false;
        logger.warn(`[AVTR-WS] underrun (${evt}) #${this.stalls}`, {
          currentTime: Number(videoEl.currentTime.toFixed(2)),
        });
      };
      videoEl.addEventListener("waiting", () => onUnderrun("waiting"));
      videoEl.addEventListener("stalled", () => onUnderrun("stalled"));

      mediaSource.addEventListener(
        "sourceopen",
        () => {
          try {
            const sb = mediaSource.addSourceBuffer(MSE_MIME);
            // "sequence" keeps a continuous live timeline even if a fragment's
            // baseMediaDecodeTime hiccups — avoids gaps that stall playback.
            sb.mode = "sequence";
            sb.addEventListener("updateend", () => this.flush());
            this.sourceBuffer = sb;
            this.flush();
          } catch (e) {
            options.onError?.(e);
            this.disconnect();
          }
        },
        { once: true }
      );

      const wsUrl =
        baseUrl.replace(/^http/i, "ws").replace(/\/+$/, "") +
        `/ws/stream?avatar_id=${encodeURIComponent(avatarId)}` +
        `&background_id=${encodeURIComponent(backgroundId)}`;

      const ws = new WebSocket(wsUrl);
      ws.binaryType = "arraybuffer";
      this.ws = ws;

      ws.onopen = () => {
        logger.log("[AVTR-WS] socket open");
        // Playback starts from _maybePlay() once the jitter buffer has filled — NOT here.
        this.setupSpeakingDetection();
        this.startRateController();
        this.startHeartbeat();
      };

      ws.onmessage = (ev) => {
        if (this.closed) return;
        const data = ev.data;
        if (data instanceof ArrayBuffer) {
          this.pending.push(data);
          this.flush();
          if (!this.connectedFired) {
            this.connectedFired = true;
            options.onConnected?.();
          }
        }
      };

      ws.onerror = (e) => {
        logger.warn("[AVTR-WS] socket error", e);
        options.onError?.(e);
      };

      ws.onclose = () => {
        logger.log("[AVTR-WS] socket closed");
        // disconnect() fires onDisconnected — do NOT call it here too, or the owner
        // would see two teardowns for one socket and schedule two reconnects.
        this.disconnect();
      };
    } catch (err) {
      options.onError?.(err);
      this.disconnect();
    }
  }

  /** Append buffered fMP4 chunks to the SourceBuffer, one at a time. */
  private flush(): void {
    const sb = this.sourceBuffer;
    if (!sb || sb.updating || this.pending.length === 0) return;
    if (!this.mediaSource || this.mediaSource.readyState !== "open") return;
    try {
      const chunk = this.pending.shift()!;
      sb.appendBuffer(chunk);
      this.maybePlay();
    } catch (e) {
      // QuotaExceeded: evict everything well behind the playhead, then retry next tick.
      logger.warn("[AVTR-WS] appendBuffer failed:", e);
      this.evictBehind();
    }
  }

  /**
   * How far behind the live edge we aim to sit. Normally TARGET_BUFFER_S, but once this connection
   * has proven it underruns, everything — the resume gate, the reseek target and the idle-pin —
   * has to agree on the larger figure. If they disagree, the pin drags the playhead back to the
   * thin buffer the stall just proved was too small, and the next stall is armed immediately.
   */
  /**
   * Seek, suppressing the `waiting` event the seek itself raises. Every correction in this class
   * must go through here — a raw `el.currentTime =` gets counted as a delivery underrun.
   */
  private seekTo(el: HTMLVideoElement, seconds: number): void {
    this.selfSeekUntil = Date.now() + SELF_SEEK_QUIET_MS;
    el.currentTime = seconds;
  }

  private liveTarget(): number {
    return this.stalls > 0 ? REBUFFER_S : TARGET_BUFFER_S;
  }

  /** Start (or resume after a stall) only once the jitter buffer has liveTarget() ready. */
  private maybePlay(): void {
    const el = this.videoEl;
    const sb = this.sourceBuffer;
    if (!el || !sb || this.playing || sb.buffered.length === 0) return;
    const end = sb.buffered.end(sb.buffered.length - 1);
    const from = el.currentTime || sb.buffered.start(0);
    if (end - from < this.liveTarget()) return; // keep buffering
    this.playing = true;
    // Also a seek, so it also has to be suppressed — this one fires on every resume after a stall.
    if (el.currentTime < sb.buffered.start(0)) this.seekTo(el, sb.buffered.start(0));
    void el
      .play()
      .then(() =>
        logger.log("[AVTR-WS] playback started", {
          stalls: this.stalls,
          buffered: Number((end - from).toFixed(2)),
        })
      )
      .catch((e) => {
        // Leaving `playing` true here wedges the client for good: maybePlay() returns early on
        // it, and so does the rate controller — so nothing ever retries or reseeks again.
        this.playing = false;
        logger.warn("[AVTR-WS] play() blocked:", e?.message || e);
      });
  }

  private evictBehind(): void {
    const sb = this.sourceBuffer;
    const el = this.videoEl;
    if (!sb || !el || sb.updating || sb.buffered.length === 0) return;
    const start = sb.buffered.start(0);
    const keepFrom = Math.max(start, el.currentTime - 2);
    if (keepFrom > start + 0.1) {
      try {
        sb.remove(start, keepFrom);
      } catch {
        // ignore; will retry
      }
    }
  }

  /**
   * Keep playback near the live edge WITHOUT jarring seeks: nudge playbackRate up when we're
   * building too much latency, ease it down when the buffer is running thin, and only hard-seek
   * on a gross desync. preservesPitch keeps the voice natural while the tempo flexes slightly.
   */
  private startRateController(): void {
    if (this.catchUpTimer) return;
    this.catchUpTimer = setInterval(() => {
      const el = this.videoEl;
      const sb = this.sourceBuffer;
      if (!el || !sb || sb.buffered.length === 0) return;

      const end = sb.buffered.end(sb.buffered.length - 1);
      let behind = end - el.currentTime; // how far the newest media is ahead of us

      // Gross-desync rescue. This MUST run before the `!playing || paused` gate below, not after:
      // the playhead only falls badly behind while it is stalled, and a stall is exactly when
      // `playing` is false — so sitting below that gate made this unreachable in the one state
      // that produces it. Drift was therefore monotonic: every underrun added, nothing subtracted,
      // and a session with 17 underruns ended up ~40s behind live with no way back.
      // Seeking here can clip a word; being permanently tens of seconds late is far worse.
      const target = this.liveTarget();
      if (behind > HARD_RESEEK_S) {
        this.seekTo(el, Math.max(sb.buffered.start(0), end - target));
        el.playbackRate = 1.0;
        this.evictBehind();
        logger.warn(
          `[AVTR-WS] hard reseek: was ${behind.toFixed(1)}s behind live (${this.stalls} underruns)`
        );
        behind = target;
      }

      // While IDLE (avatar not speaking), pin to the live edge so the NEXT utterance starts on
      // time. The persistent stream otherwise drifts behind live during silence, and the gentle
      // 1.03–1.08x catch-up never recovers it — speech the backend renders on time then plays
      // seconds late. Skipping idle frames is inaudible; seeking mid-sentence is not, which is why
      // this is gated on `speaking` and the mid-speech case is left to HARD_RESEEK_S above.
      //
      // Like the reseek, this has to run BEFORE the `!playing || paused` gate. Drift is built while
      // stalled, so a correction that only runs while playing cleanly can never collect it — and
      // between turns it is the cheapest correction available, because there are no words to clip.
      const now = Date.now();
      if (
        !this.speaking &&
        behind > target + IDLE_PIN_SLACK_S &&
        now - this.lastPinAt > PIN_COOLDOWN_MS
      ) {
        this.lastPinAt = now;
        this.seekTo(el, Math.max(sb.buffered.start(0), end - target));
        el.playbackRate = 1.0;
        this.evictBehind();
        this.maybePlay();
        logger.log(`[AVTR-WS] idle pin: ${behind.toFixed(1)}s -> ${target.toFixed(1)}s behind live`);
        return;
      }

      // (Re)start playback if it's gated/stalled and the buffer has recovered.
      this.maybePlay();
      if (!this.playing || el.paused) return;

      if (behind > MAX_BUFFER_S) {
        el.playbackRate = 1.08; // too much latency: drain toward live
      } else if (behind > target + 0.3) {
        el.playbackRate = 1.03; // slightly ahead: gentle drain
      } else if (behind < 0.2) {
        el.playbackRate = 0.95; // buffer thinning: slow a touch to rebuild
      } else {
        el.playbackRate = 1.0;
      }
      this.evictBehind();
    }, 500);
  }

  private startHeartbeat(): void {
    if (this.heartbeatTimer) return;
    this.heartbeatTimer = setInterval(() => {
      try {
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.ws.send("ping");
        }
      } catch {
        // The close/error handlers own reconnect.
      }
    }, HEARTBEAT_MS);
  }

  /** WebRTC parity no-ops: the WS transport captures no mic. */
  startUserAudio(): void {}
  stopUserAudio(): void {}

  /** Barge-in: local mute for instant silence + tell the streamer to drop current speech. */
  async interrupt(): Promise<void> {
    if (this.videoEl) this.videoEl.muted = true;
    if (!this.baseUrl) return;
    try {
      await fetch(`${this.baseUrl}/interrupt`, { method: "POST" });
    } catch (e) {
      logger.warn("[AVTR-WS] interrupt failed:", e);
    }
  }

  /** Unmute the avatar audio (call from a user gesture to satisfy autoplay policy). */
  unmute(): void {
    if (this.videoEl) {
      this.videoEl.muted = false;
      void this.videoEl.play().catch(() => undefined);
    }
    void this.audioCtx?.resume().catch(() => undefined);
  }

  isConnected(): boolean {
    return !!this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  private setupSpeakingDetection(): void {
    if (this.speakingSetup || !this.videoEl) return;
    try {
      // Reuse the element's shared context + source (created once per <video>). We only add a
      // fresh analyser as a TAP off the source — the source already feeds destination, so audio
      // keeps playing; the analyser has no onward connection, it just reads levels.
      const graph = getElementAudioGraph(this.videoEl);
      if (!graph) return;
      this.audioCtx = graph.ctx;
      this.audioSource = graph.source;
      const analyser = graph.ctx.createAnalyser();
      analyser.fftSize = 512;
      graph.source.connect(analyser);
      this.analyser = analyser;
      void graph.ctx.resume().catch(() => undefined);

      const data = new Uint8Array(analyser.fftSize);
      const THRESHOLD = 0.02;
      const HANGOVER_MS = 500;
      this.speakingSetup = true;
      this.levelTimer = setInterval(() => {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        const now = Date.now();
        if (rms > THRESHOLD) {
          this.silenceSince = 0;
          if (!this.speaking) {
            this.speaking = true;
            this.onSpeaking?.(true);
          }
        } else if (this.speaking) {
          if (this.silenceSince === 0) this.silenceSince = now;
          if (now - this.silenceSince > HANGOVER_MS) {
            this.speaking = false;
            this.onSpeaking?.(false);
          }
        }
      }, 80);
    } catch (e) {
      logger.warn("[AVTR-WS] speaking detection unavailable:", e);
    }
  }

  disconnect(): void {
    if (this.closed) return;
    this.closed = true;
    if (this.levelTimer) clearInterval(this.levelTimer);
    if (this.catchUpTimer) clearInterval(this.catchUpTimer);
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.levelTimer = null;
    this.catchUpTimer = null;
    this.heartbeatTimer = null;
    // Remove only OUR analyser tap; keep the shared context + source alive so the next client
    // (avatar switch) can reuse them — do NOT close the context (closing it mutes the element
    // forever, since createMediaElementSource can't be re-run on the same element).
    if (this.analyser && this.audioSource) {
      try {
        this.audioSource.disconnect(this.analyser);
      } catch {
        // ignore
      }
    }
    this.analyser = null;
    this.audioSource = null;
    this.audioCtx = null;
    this.speakingSetup = false;
    if (this.speaking) {
      this.speaking = false;
      this.onSpeaking?.(false);
    }
    try {
      if (this.ws) {
        this.ws.onopen = null;
        this.ws.onmessage = null;
        this.ws.onerror = null;
        this.ws.onclose = null;
        if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
          this.ws.close();
        }
      }
      if (this.mediaSource && this.mediaSource.readyState === "open") {
        try {
          this.mediaSource.endOfStream();
        } catch {
          // ignore
        }
      }
    } catch {
      // best-effort teardown
    } finally {
      this.ws = null;
      this.sourceBuffer = null;
      this.mediaSource = null;
      this.pending = [];
      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl);
        this.objectUrl = null;
      }
      if (this.videoEl) {
        this.videoEl.removeAttribute("src");
        this.videoEl.load();
      }
      this.videoEl = null;
      // Single teardown notification, after the client is fully torn down. Every route into
      // disconnect() reaches this — a clean socket close, an MSE failure, the connect() catch,
      // or the owner disposing us — so the owner can never be left holding a dead client it
      // thinks is live. The `closed` guard at the top makes it fire exactly once.
      const notify = this.onDisconnected;
      this.onDisconnected = undefined;
      notify?.();
    }
  }
}
